import { css } from '@emotion/react';
import { useTranslation } from '@frontend/i18n';
import { useTranslationTokens } from '@frontend/i18n-tokens';
import { Icon } from '@frontend/icons';
import { theme } from '@frontend/theme';
import {
  DropdownField,
  Heading,
  MessageTemplate,
  rgba,
  SwitchField,
  Text,
  UseFormReturnType,
} from '@frontend/design-system';
import { INITIAL_DIRECT_BOOKING_MESSAGE_TEMPLATE } from '../../constants';
import { DropdownOption } from '../../types';
import { AppointmentSlots } from './appointment-slots';
import { apptDetailsFormFields } from './use-waitlist-appointment-details-form';

interface Props {
  timezoneAbbr: string;
  form: UseFormReturnType<typeof apptDetailsFormFields, keyof typeof apptDetailsFormFields>;
  providerOptions: DropdownOption[];
  appointmentTypeOptions: DropdownOption[];
  activeSlotCount: number;
  onSlotCountChange: (count: number) => void;
}

export const WaitlistAppointmentDetails = ({
  timezoneAbbr,
  form,
  providerOptions,
  appointmentTypeOptions,
  activeSlotCount,
  onSlotCountChange,
}: Props) => {
  const { t } = useTranslation('schedule-quick-fill');
  const tokens = useTranslationTokens();

  return (
    <section css={containerStyle}>
      <header css={{ display: 'flex', alignItems: 'center', gap: theme.spacing(1), marginBottom: theme.spacing(3) }}>
        <Icon name='calendar' />
        <Heading level={3}>{t('Appointment Details')}</Heading>
      </header>
      <div css={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(2) }}>
        <AppointmentSlots
          timezoneAbbr={timezoneAbbr}
          form={form}
          activeSlotCount={activeSlotCount}
          onSlotCountChange={onSlotCountChange}
        />
        <div css={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(1) }}>
          <Text weight='bold'>{t('Direct Scheduling')}</Text>
          <Text color='subdued'>
            {t(
              'When Direct Scheduling is enabled, the {{contact}} will receive a link where they can automatically book the appointment.',
              { contact: tokens.individualContact.toLowerCase() }
            )}
          </Text>
        </div>
        <SwitchField
          {...form.getFieldProps('directScheduling')}
          name='directScheduling'
          label={t('Enable Direct Scheduling')}
        />
        {form.values.directScheduling ? (
          <>
            <DropdownField
              disabled={!form.values.directScheduling}
              {...form.getFieldProps('provider')}
              name='provider'
              label={t('Provider')}
            >
              {providerOptions.map((provider) => (
                <DropdownField.Option key={provider.value} value={provider.value}>
                  {provider.label}
                </DropdownField.Option>
              ))}
            </DropdownField>
            <DropdownField
              disabled={!form.values.directScheduling}
              {...form.getFieldProps('appointmentType')}
              name='appointmentType'
              label={t('Appointment Type')}
            >
              {appointmentTypeOptions.map((appointmentType) => (
                <DropdownField.Option key={appointmentType.value} value={appointmentType.value}>
                  {appointmentType.label}
                </DropdownField.Option>
              ))}
            </DropdownField>

            <MessageTemplate initialTemplate={INITIAL_DIRECT_BOOKING_MESSAGE_TEMPLATE} tags={[]} draggableTags isNewTag>
              <div css={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(0.5) }}>
                <Text size='small' color='subdued'>
                  {t('Message Preview')}
                </Text>
                <MessageTemplate.Preview
                  css={{ background: rgba(theme.colors.primary50, 0.1), p: { fontSize: theme.font.size.medium } }}
                />
              </div>
            </MessageTemplate>
          </>
        ) : (
          <>Editable Message Template goes here</>
        )}
      </div>
    </section>
  );
};

const containerStyle = css({
  border: `1px solid ${theme.colors.neutral20}`,
  borderRadius: theme.borderRadius.small,
  padding: theme.spacing(3),
  boxShadow: theme.shadows.light,
  maxWidth: 450,
});
